import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import useRefreshOnFocus from '../useRefreshOnFocus';
import AddToWatchlist from '../components/AddToWatchlist';
import CashEntryModal, { CASH_TYPES, cashTypeLabel, cashSign } from '../components/CashEntryModal';
import { EditIcon, DeleteIcon, WatchIcon, ChartIcon, BellIcon } from '../components/Icons';

const EMPTY_FORM = {
  account_id: '', symbol: '', transaction_type: 'buy',
  date: new Date().toISOString().split('T')[0],
  quantity: '', price_per_share: '', total_amount: '', notes: ''
};

const roundQty = (n) => Math.round(n * 1e6) / 1e6;

export default function Transactions({ navParams = {}, onNavigate }) {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  // A buy the API refused for lack of cash: { date, shortfall, available }.
  const [shortfall, setShortfall] = useState(null);
  // The buy set aside ({ form, editId }) while a missed sell is entered in its place.
  const [pendingBuy, setPendingBuy] = useState(null);
  // A deposit or opening balance being added from inside the transaction modal.
  const [helperCash, setHelperCash] = useState(null);
  // Selling: the lots (buys) available in the chosen account + symbol, and the
  // quantity being sold from each, keyed by the lot's buy transaction id.
  const [lots, setLots] = useState([]);
  const [lotQty, setLotQty] = useState({});
  const [filterAccount, setFilterAccount] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [watchSymbol, setWatchSymbol] = useState(null);
  const [tab, setTab] = useState(navParams.tab === 'cash' ? 'cash' : 'trades');
  const [cash, setCash] = useState([]);
  const [cashBalances, setCashBalances] = useState({});
  const [filterCashType, setFilterCashType] = useState('');
  const [cashModalEntry, setCashModalEntry] = useState(null);
  const [cashSortField, setCashSortField] = useState('date');
  const [cashSortDir, setCashSortDir] = useState('desc');

  useEffect(() => {
    if (navParams.symbol) setFilterSymbol(navParams.symbol);
    if (navParams.transaction_type) setFilterType(navParams.transaction_type);
    if (navParams.account_id) setFilterAccount(String(navParams.account_id));
    if (navParams.tab) setTab(navParams.tab === 'cash' ? 'cash' : 'trades');
  }, [navParams.symbol, navParams.transaction_type, navParams.account_id, navParams.tab]);

  const loadAll = () => api.getTransactions({}).then(setAllTransactions);

  const load = () => Promise.all([
    api.getTransactions({
      ...(filterAccount ? { account_id: filterAccount } : {}),
      ...(filterType ? { transaction_type: filterType } : {}),
      ...(filterSymbol ? { symbol: filterSymbol } : {}),
    }),
    api.getAccounts()
  ]).then(([t, a]) => { setTransactions(t); setAccounts(a); });

  const loadCash = () => Promise.all([
    api.getCash({
      ...(filterAccount ? { account_id: filterAccount } : {}),
      ...(filterCashType ? { type: filterCashType } : {}),
    }),
    api.getCashBalances(),
  ]).then(([c, b]) => { setCash(c); setCashBalances(b); });

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { load(); }, [filterAccount, filterType, filterSymbol]);
  useEffect(() => { loadCash(); }, [filterAccount, filterCashType]);
  useRefreshOnFocus(() => { load(); loadAll(); loadCash(); });

  const availableSymbols = () => {
    let filtered = allTransactions;
    if (filterAccount) filtered = filtered.filter(t => String(t.account_id) === String(filterAccount));
    if (filterType) filtered = filtered.filter(t => t.transaction_type === filterType);
    return [...new Set(filtered.map(t => t.symbol))].sort();
  };

  const isSell = form.transaction_type === 'sell';

  useEffect(() => {
    setLots([]);
    if (!showModal || !isSell || !form.account_id || !form.symbol) return;
    let cancelled = false;
    api.getSellLots(form.account_id, form.symbol, editId)
      .then(rows => { if (!cancelled) setLots(rows || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showModal, isSell, form.account_id, form.symbol, editId]);

  // A sale can only draw on lots bought on or before the sale date.
  const sellableLots = (date) => lots.filter(l => l.date <= date);
  const selectedLots = (qtyMap, date) => sellableLots(date)
    .map(l => ({ ...l, sell: parseFloat(qtyMap[l.buy_transaction_id]) || 0 }))
    .filter(l => l.sell > 0);
  const lotTotal = (qtyMap, date) => roundQty(selectedLots(qtyMap, date).reduce((sum, l) => sum + l.sell, 0));

  const visibleLots = sellableLots(form.date);
  const chosenLots = selectedLots(lotQty, form.date);
  // A sell's quantity is whatever was picked from the lots.
  const sellQty = lotTotal(lotQty, form.date);
  const sellCost = chosenLots.reduce((sum, l) => sum + l.sell * l.cost_per_share, 0);

  const clearError = () => { setFormError(''); setShortfall(null); };

  const updateForm = (field, val) => {
    const updated = { ...form, [field]: val };
    const sell = updated.transaction_type === 'sell';
    if (field === 'quantity' || field === 'price_per_share' || (sell && (field === 'date' || field === 'transaction_type'))) {
      const q = sell ? lotTotal(lotQty, updated.date) : parseFloat(updated.quantity);
      const p = parseFloat(updated.price_per_share);
      if (!isNaN(q) && !isNaN(p)) updated.total_amount = (q * p).toFixed(2);
    }
    if (field === 'account_id' || field === 'symbol') setLotQty({});
    clearError();
    setForm(updated);
  };

  const setLot = (id, val) => {
    const next = { ...lotQty, [id]: val };
    setLotQty(next);
    clearError();
    const p = parseFloat(form.price_per_share);
    if (!isNaN(p)) setForm(f => ({ ...f, total_amount: (lotTotal(next, f.date) * p).toFixed(2) }));
  };

  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM); setLotQty({}); clearError(); setPendingBuy(null); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setPendingBuy(null); clearError(); };
  const openEdit = (t) => {
    setEditId(t.id);
    setLotQty(Object.fromEntries((t.lot_allocations || []).map(a => [a.buy_transaction_id, a.quantity])));
    clearError();
    setPendingBuy(null);
    setForm({
      account_id: t.account_id,
      symbol: t.symbol,
      transaction_type: t.transaction_type,
      date: t.date,
      quantity: t.quantity,
      price_per_share: t.price_per_share,
      total_amount: t.total_amount,
      notes: t.notes || ''
    });
    setShowModal(true);
  };

  const submit = async () => {
    if (!form.account_id) return setFormError('Select an account.');
    if (!form.symbol) return setFormError('Enter a symbol.');
    if (isSell) {
      if (!(sellQty > 0)) return setFormError('Select the lots being sold and how many shares from each.');
      const over = chosenLots.find(l => l.sell > l.available + 1e-6);
      if (over) return setFormError(`Only ${over.available} shares are left in the lot bought ${over.date}.`);
    }
    const payload = {
      ...form,
      account_id: parseInt(form.account_id),
      quantity: isSell ? sellQty : parseFloat(form.quantity),
      price_per_share: parseFloat(form.price_per_share),
      total_amount: parseFloat(form.total_amount),
      ...(isSell ? { lot_allocations: chosenLots.map(l => ({ buy_transaction_id: l.buy_transaction_id, quantity: l.sell })) } : {}),
    };
    try {
      if (editId) {
        await api.updateTransaction(editId, payload);
      } else {
        await api.createTransaction(payload);
      }
    } catch (e) {
      setShortfall(e.detail?.code === 'insufficient_cash' ? e.detail : null);
      return setFormError(e.message);
    }
    if (pendingBuy) restoreBuy();
    else setShowModal(false);
    load();
    loadAll();
    loadCash();
  };

  // The account chosen in the modal: new accounts need an opening balance before
  // their first trade; older ones that never got one are only nudged.
  const formCash = form.account_id ? cashBalances[form.account_id] : null;
  const needsOpening = !!formCash && !formCash.has_opening_balance;
  const openingRequired = needsOpening && !editId
    && !allTransactions.some(t => String(t.account_id) === String(form.account_id));

  const setOpening = () => {
    const suggested = formCash.suggested_opening_balance;
    setHelperCash({
      entry: {
        account_id: form.account_id,
        type: 'opening_balance',
        amount: suggested ? suggested.amount : '',
        date: suggested ? suggested.date : form.date,
      },
      hint: suggested
        ? `Prefilled with the smallest balance that keeps cash from going negative, dated at the account's first activity (${suggested.date}). Adjust to match your statement.`
        : 'Enter the cash the account held before its first trade (0 if it started empty).',
    });
  };

  const addDeposit = () => setHelperCash({
    entry: { account_id: form.account_id, type: 'deposit', amount: shortfall.shortfall, date: shortfall.date },
    hint: `Prefilled with the shortfall, dated ${shortfall.date} when cash would first go negative. Adjust to match your statement.`,
  });

  // Swap the modal over to the missed sell; the buy comes back when it's saved or abandoned.
  const enterMissedSell = () => {
    setPendingBuy({ form, editId });
    setEditId(null);
    setLotQty({});
    setForm({ ...EMPTY_FORM, account_id: form.account_id, transaction_type: 'sell', date: shortfall.date });
    clearError();
  };

  const restoreBuy = () => {
    setEditId(pendingBuy.editId);
    setForm(pendingBuy.form);
    setLotQty({});
    setPendingBuy(null);
    clearError();
  };

  const remove = async (id) => {
    if (window.confirm('Delete this transaction?')) {
      try {
        await api.deleteTransaction(id);
      } catch (e) {
        return window.alert(e.message);
      }
      load();
      loadAll();
      loadCash();
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortTh = ({ field, label }) => (
    <th onClick={() => toggleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const sorted = [...transactions].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const removeCash = async (id) => {
    if (window.confirm('Delete this cash entry?')) {
      try {
        await api.deleteCash(id);
      } catch (e) {
        return window.alert(e.message);
      }
      loadCash();
    }
  };

  const toggleCashSort = (field) => {
    if (cashSortField === field) setCashSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCashSortField(field); setCashSortDir('asc'); }
  };

  const CashSortTh = ({ field, label }) => (
    <th onClick={() => toggleCashSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {cashSortField === field ? (cashSortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const fmt = (n) => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtSigned = (n) => (n < 0 ? '-' : '') + fmt(Math.abs(n));

  // Sort cash by signed amount so withdrawals/fees order below deposits.
  const cashSortValue = (c, field) => field === 'amount' ? c.signed : (c[field] ?? '');
  // The Cash tab is the account's full cash ledger: recorded cash entries plus the
  // cash side of every trade (buy = cash out, sell = cash in). Trade rows are
  // derived from the trade itself — never stored twice — so they follow its edits
  // and deletion, and are changed by editing the trade.
  const cashRows = [
    ...cash.map(c => ({ ...c, key: `c${c.id}`, signed: cashSign(c.type) * c.amount })),
    ...allTransactions
      .filter(t => (!filterAccount || String(t.account_id) === String(filterAccount))
        && (!filterCashType || t.transaction_type === filterCashType))
      .map(t => ({
        key: `t${t.id}`,
        trade: t,
        date: t.date,
        type: t.transaction_type,
        signed: (t.transaction_type === 'buy' ? -1 : 1) * t.total_amount,
        symbol: t.symbol,
        account_id: t.account_id,
        notes: `${t.quantity} @ ${fmt(t.price_per_share)}${t.notes ? ' — ' + t.notes : ''}`,
      })),
  ];
  const sortedCash = [...cashRows].sort((a, b) => {
    const av = cashSortValue(a, cashSortField), bv = cashSortValue(b, cashSortField);
    if (typeof av === 'string') return cashSortDir === 'asc' ? String(av).localeCompare(bv) : String(bv).localeCompare(av);
    return cashSortDir === 'asc' ? av - bv : bv - av;
  });

  const accountName = (id) => accounts.find(a => a.id === id)?.name || id;
  const symbols = availableSymbols();

  useEffect(() => {
    if (filterSymbol && symbols.length > 0 && !symbols.includes(filterSymbol)) {
      setFilterSymbol('');
    }
  }, [filterAccount, filterType]);
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Transactions</h1>
        {tab === 'trades'
          ? <button className="btn" onClick={openAdd}>+ Add Transaction</button>
          : <button className="btn" onClick={() => setCashModalEntry({ account_id: filterAccount || '' })}>+ Add Cash</button>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className={tab === 'trades' ? 'btn' : 'btn-ghost'} onClick={() => setTab('trades')}>Trades ({transactions.length})</button>
        <button className={tab === 'cash' ? 'btn' : 'btn-ghost'} onClick={() => setTab('cash')}>Cash ({cashRows.length})</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ width: 'auto' }} value={filterAccount} onChange={e => { setFilterAccount(e.target.value); setFilterSymbol(''); }}>
          <option value="">All Accounts</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {tab === 'cash' && (
          <select style={{ width: 'auto' }} value={filterCashType} onChange={e => setFilterCashType(e.target.value)}>
            <option value="">All Types</option>
            {CASH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>
        )}
        {tab === 'cash' && (filterAccount || filterCashType) && (
          <button className="btn-ghost" onClick={() => { setFilterAccount(''); setFilterCashType(''); }}>
            Clear Filters
          </button>
        )}
        {tab === 'trades' && (
          <>
            <select style={{ width: 'auto' }} value={filterType} onChange={e => { setFilterType(e.target.value); setFilterSymbol(''); }}>
              <option value="">Buy & Sell</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
            <select style={{ width: 'auto' }} value={filterSymbol} onChange={e => setFilterSymbol(e.target.value)}>
              <option value="">All Symbols</option>
              {symbols.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}
        {tab === 'trades' && (filterAccount || filterType || filterSymbol) && (
          <button className="btn-ghost" onClick={() => { setFilterAccount(''); setFilterType(''); setFilterSymbol(''); }}>
            Clear Filters
          </button>
        )}
        {tab === 'cash' && filterAccount && cashBalances[filterAccount] && (
          <span
            style={{
              marginLeft: 'auto',
              padding: '6px 14px',
              borderRadius: 6,
              border: `1px solid ${cashBalances[filterAccount].balance < 0 ? 'var(--loss)' : 'var(--accent)'}`,
              background: cashBalances[filterAccount].balance < 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(59, 130, 246, 0.12)',
              fontSize: 15,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Cash balance: <span className={`mono ${cashBalances[filterAccount].balance < 0 ? 'loss' : ''}`}>{fmtSigned(cashBalances[filterAccount].balance)}</span>
          </span>
        )}
      </div>

      {tab === 'cash' && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <CashSortTh field="date" label="Date" />
                <CashSortTh field="type" label="Type" />
                <CashSortTh field="amount" label="Amount" />
                <CashSortTh field="symbol" label="Symbol" />
                <CashSortTh field="account_id" label="Account" />
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedCash.map(c => (
                <tr key={c.key}>
                  <td className="muted">{c.date}</td>
                  <td>{c.trade ? (c.type === 'buy' ? 'Buy' : 'Sell') : cashTypeLabel(c.type)}</td>
                  <td className={`mono amount ${c.signed < 0 ? 'loss' : 'gain'}`}>{fmtSigned(c.signed)}</td>
                  <td className="mono">{c.symbol || <span className="muted">—</span>}</td>
                  <td className="muted">{accountName(c.account_id)}</td>
                  <td className="muted">{c.notes}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {c.trade ? (
                      <button className="icon-btn" title="Edit trade" aria-label="Edit trade" onClick={() => openEdit(c.trade)}><EditIcon /></button>
                    ) : (
                      <>
                        <button className="icon-btn" style={{ marginRight: 6 }} title="Edit" aria-label="Edit" onClick={() => setCashModalEntry(c)}><EditIcon /></button>
                        <button className="icon-btn" title="Delete" aria-label="Delete" onClick={() => removeCash(c.id)}><DeleteIcon /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {sortedCash.length === 0 && (
                <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>No cash entries found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'trades' && <div className="card">
        <table>
          <thead>
            <tr>
              <SortTh field="date" label="Date" />
              <SortTh field="symbol" label="Symbol" />
              <SortTh field="transaction_type" label="Type" />
              <SortTh field="quantity" label="Qty" />
              <SortTh field="price_per_share" label="Price" />
              <SortTh field="total_amount" label="Total" />
              <SortTh field="account_id" label="Account" />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(t => (
              <tr key={t.id}>
                <td className="muted">{t.date}</td>
                <td><strong className="mono" style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => onNavigate && onNavigate('charts', { symbol: t.symbol })}>{t.symbol}</strong></td>
                <td>
                  <span className={t.transaction_type === 'buy' ? 'gain' : 'loss'}>{t.transaction_type.toUpperCase()}</span>
                  {t.unassigned_quantity > 0 && (
                    <span
                      className="muted"
                      style={{ fontSize: 12, marginLeft: 8, cursor: 'pointer', textDecoration: 'underline' }}
                      title="No lots are selected for this sell, so its cost basis is provisional (oldest lots first). Click to choose lots."
                      onClick={() => openEdit(t)}
                    >
                      Lots needed
                    </span>
                  )}
                </td>
                <td className="mono amount">{t.quantity}</td>
                <td className="mono amount">{fmt(t.price_per_share)}</td>
                <td className="mono amount">{fmt(t.total_amount)}</td>
                <td className="muted">{accountName(t.account_id)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="icon-btn" style={{ marginRight: 6 }} title="Watch" aria-label="Watch" onClick={() => setWatchSymbol(t.symbol)}><WatchIcon /></button>
                  <button className="icon-btn" style={{ marginRight: 6 }} title="Chart" aria-label="Chart" onClick={() => onNavigate && onNavigate('charts', { symbol: t.symbol })}><ChartIcon /></button>
                  <button className="icon-btn" style={{ marginRight: 6 }} title="Alert" aria-label="Alert" onClick={() => onNavigate && onNavigate('alerts', { symbol: t.symbol })}><BellIcon /></button>
                  <button className="icon-btn" style={{ marginRight: 6 }} title="Edit" aria-label="Edit" onClick={() => openEdit(t)}><EditIcon /></button>
                  <button className="icon-btn" title="Delete" aria-label="Delete" onClick={() => remove(t.id)}><DeleteIcon /></button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 32 }}>No transactions found.</td></tr>
            )}
          </tbody>
        </table>
      </div>}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{editId ? 'Edit Transaction' : 'Add Transaction'}</div>
            {pendingBuy && (
              <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                Entering a missed sell. Your buy of {pendingBuy.form.symbol} is kept and comes back once this is saved.
              </div>
            )}
            <div className="form-group">
              <label>Account</label>
              <select value={form.account_id} onChange={e => updateForm('account_id', e.target.value)}>
                <option value="">Select account</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Symbol</label>
                <input value={form.symbol} onChange={e => updateForm('symbol', e.target.value.toUpperCase())} placeholder="AAPL" />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={form.transaction_type} onChange={e => updateForm('transaction_type', e.target.value)}>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={form.date} onChange={e => updateForm('date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Quantity</label>
                {isSell
                  ? <input type="number" value={sellQty || ''} readOnly placeholder="From lots below" title="Set by the lots selected below" />
                  : <input type="number" value={form.quantity} onChange={e => updateForm('quantity', e.target.value)} placeholder="0" />}
              </div>
              <div className="form-group">
                <label>Price Per Share</label>
                <input type="number" value={form.price_per_share} onChange={e => updateForm('price_per_share', e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label>Total Amount</label>
                <input type="number" value={form.total_amount} onChange={e => updateForm('total_amount', e.target.value)} placeholder="0.00" />
              </div>
            </div>
            {isSell && (
              <div className="form-group">
                <label>Lots to sell</label>
                {!form.account_id || !form.symbol ? (
                  <div className="muted" style={{ fontSize: 13 }}>Choose an account and symbol to see the lots you can sell.</div>
                ) : visibleLots.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>No lots of {form.symbol} are available in this account on {form.date}.</div>
                ) : (
                  <>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Bought</th>
                          <th>Cost/Share</th>
                          <th>Available</th>
                          <th>Sell Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleLots.map(l => {
                          const entered = parseFloat(lotQty[l.buy_transaction_id]) || 0;
                          return (
                            <tr key={l.buy_transaction_id}>
                              <td className="muted">{l.date}</td>
                              <td className="mono amount">{fmt(l.cost_per_share)}</td>
                              <td className="mono amount">
                                <span
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  title="Sell the whole lot"
                                  onClick={() => setLot(l.buy_transaction_id, l.available)}
                                >
                                  {l.available}
                                </span>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  max={l.available}
                                  value={lotQty[l.buy_transaction_id] ?? ''}
                                  onChange={e => setLot(l.buy_transaction_id, e.target.value)}
                                  placeholder="0"
                                  style={entered > l.available + 1e-6 ? { borderColor: 'var(--loss)' } : undefined}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                    {editId && Number(form.quantity) !== sellQty && (
                      <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                        This sale was recorded as <span className="mono">{form.quantity}</span> shares.
                      </div>
                    )}
                    <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                      Selling <span className="mono">{sellQty}</span> shares · cost basis <span className="mono">{fmt(sellCost)}</span>
                      {sellQty > 0 && !isNaN(parseFloat(form.total_amount)) && (
                        <> · realized gain <span className={`mono ${parseFloat(form.total_amount) - sellCost >= 0 ? 'gain' : 'loss'}`}>{fmtSigned(parseFloat(form.total_amount) - sellCost)}</span></>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="form-group">
              <label>Notes (optional)</label>
              <input value={form.notes} onChange={e => updateForm('notes', e.target.value)} placeholder="e.g. dividend reinvestment" />
            </div>
            {needsOpening && (
              <div className={openingRequired ? 'loss' : 'muted'} style={{ fontSize: 13, marginBottom: 8 }}>
                {openingRequired
                  ? 'This account needs an opening cash balance before transactions can be entered.'
                  : 'This account has no opening cash balance yet. Entries are still accepted; set one so its cash is right.'}{' '}
                <button className="btn-ghost" style={{ fontSize: 12, padding: '2px 6px' }} onClick={setOpening}>
                  Set opening balance
                </button>
              </div>
            )}
            {formError && <div className="loss" style={{ fontSize: 13, marginBottom: 8 }}>{formError}</div>}
            {shortfall && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button className="btn-ghost" onClick={addDeposit}>Add cash deposit</button>
                <button className="btn-ghost" onClick={enterMissedSell}>Enter a sell</button>
              </div>
            )}
            <div className="modal-actions">
              {pendingBuy
                ? <button className="btn-ghost" onClick={restoreBuy}>Back to buy</button>
                : <button className="btn-ghost" onClick={closeModal}>Cancel</button>}
              <button className="btn" onClick={submit} disabled={openingRequired}>{editId ? 'Save Changes' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      {cashModalEntry && (
        <CashEntryModal
          entry={cashModalEntry}
          accounts={accounts}
          balances={cashBalances}
          onClose={() => setCashModalEntry(null)}
          onSaved={() => { setCashModalEntry(null); loadCash(); }}
        />
      )}
      {helperCash && (
        <CashEntryModal
          entry={helperCash.entry}
          accounts={accounts}
          balances={cashBalances}
          lockAccount
          hint={helperCash.hint}
          onClose={() => setHelperCash(null)}
          onSaved={() => { setHelperCash(null); clearError(); loadCash(); }}
        />
      )}
      {watchSymbol && <AddToWatchlist symbol={watchSymbol} onClose={() => setWatchSymbol(null)} />}
    </div>
  );
}